import React from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';

interface Technology {
  name: string;
  color?: string;
  logo_path?: string;
}

interface TechnologyStackProps {
  technologies: any;
  maxVisible?: number;
}

export const TechnologyStack: React.FC<TechnologyStackProps> = ({ 
  technologies,
  maxVisible = 3
}) => {
  // Ensure technologies is an array
  const techArray = React.useMemo(() => {
    if (!technologies) return [];
    
    // If it's already an array, use it
    if (Array.isArray(technologies)) return technologies;
    
    // If it's a string, try to parse it as JSON
    if (typeof technologies === 'string') {
      try {
        const parsed = JSON.parse(technologies);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    
    // If it's an object but not an array, convert to array
    if (typeof technologies === 'object') {
      return Object.values(technologies);
    }
    
    return [];
  }, [technologies]);
  
  const visibleTechs = techArray.slice(0, maxVisible);
  const remainingCount = techArray.length - maxVisible;
  const hasMore = remainingCount > 0;

  return (
    <div className="flex items-center space-x-2">
      {visibleTechs.map((tech, index) => {
        // Skip if tech is not an object or doesn't have a name
        if (!tech || typeof tech !== 'object' || !tech.name) {
          return null;
        }
        
        // Default color if not provided
        const color = tech.color || 'blue';
        
        return (
          <Tooltip.Provider key={index}>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <div 
                  className={`w-8 h-8 rounded bg-${color}-500/10 border border-${color}-500/20 p-1 cursor-help transition-colors hover:bg-${color}-500/20`}
                >
                  {tech.logo_path ? (
                    <img 
                      src={`https://www.wappalyzer.com/images/icons/${tech.logo_path}`} 
                      className="w-full h-full object-contain" 
                      alt={tech.name} 
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs font-bold text-blue-400">
                      {tech.name.substring(0, 1)}
                    </div>
                  )}
                </div>
              </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className="px-2 py-1 rounded-lg bg-[#1A1F2E] border border-gray-800 text-xs text-white shadow-xl"
                sideOffset={5}
              >
                {tech.name} {tech.version}
                <Tooltip.Arrow className="fill-[#1A1F2E]" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      );
      })}

      {hasMore && (
        <Tooltip.Provider>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <div className="w-6 h-6 rounded bg-gray-500/10 border border-gray-500/20 p-1 cursor-help transition-colors hover:bg-gray-500/20 flex items-center justify-center text-xs font-medium text-gray-400">
                +{remainingCount}
              </div>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className="px-3 py-1.5 rounded-lg bg-[#1A1F2E] border border-gray-800 text-xs text-white shadow-xl"
                sideOffset={5}
              >
                <div className="space-y-1">
                  <div className="font-medium">All Technologies:</div>
                  <div className="text-gray-400">
                    {technologies.map((tech: any, index: number) => (
                      <React.Fragment key={index}>
                        • {tech.name} {tech.version}<br />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
                <Tooltip.Arrow className="fill-[#1A1F2E]" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      )}
    </div>
  );
};
